/**
 * BillingInvoiceNumberBackfill — one-click admin utility to backfill `Invoice Number` on already
 * posted Billing Run Line Items that predate the Cortex port ever writing it
 * (api/admin/backfill-billing-invoice-numbers.js). Loops the batched endpoint by Airtable page
 * cursor and shows running progress. Idempotent — re-running skips records that already have a
 * number. Matches are only written when confident (see the endpoint); everything else is reported
 * as "ambiguous" or "no candidate" for manual review in Airtable.
 *
 * One-off maintenance utility — safe to remove once the current backlog of posted-but-blank line
 * items has been cleared out (new postings capture Invoice Number automatically going forward).
 */
import { useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

export default function BillingInvoiceNumberBackfill({ dark }) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)     // { total }
  const [progress, setProgress] = useState(null) // { processed, matched, ambiguous, noCandidate }
  const [err, setErr] = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function token() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

  async function check() {
    setBusy(true); setErr(null)
    try {
      const res = await fetch('/api/admin/backfill-billing-invoice-numbers', { headers: { Authorization: 'Bearer ' + await token() } })
      const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
      setStatus(d)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function run() {
    if (!(await confirmDialog({
      title: 'Backfill invoice numbers?',
      message: 'For each posted line item missing an invoice number, match it to the most likely Core invoice by date and write it in. Only confident matches are written — anything ambiguous is left for manual review. Safe to re-run.',
      confirmLabel: 'Run backfill',
    }))) return
    setBusy(true); setErr(null)
    const totals = { processed: 0, matched: 0, ambiguous: 0, noCandidate: 0 }
    try {
      let cursor = null, done = false, guard = 0
      while (!done && guard++ < 300) {
        const t = await token()
        const res = await fetch('/api/admin/backfill-billing-invoice-numbers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
          body: JSON.stringify({ cursor, limit: 10 }),
        })
        const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
        totals.processed += d.processed; totals.matched += d.matched
        totals.ambiguous += d.ambiguous; totals.noCandidate += d.noCandidate
        setProgress({ ...totals })
        cursor = d.nextCursor; done = d.done
      }
      check()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 680 }}>
        Billing Run Line Items never got their Invoice Number written by the Cortex port — the
        payment-followup steps in the Billing Run need it to look a specific invoice back up in
        Core. This matches each already-posted, still-blank line item to its most likely invoice by
        date and fills it in. Only confident matches are written; ambiguous ones are left blank for
        manual review in Airtable. Idempotent — re-run any time; already-filled records are skipped.
        One-off maintenance utility.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn(false)} disabled={busy} onClick={check}>{busy ? 'Working…' : 'Check status'}</button>
        <button style={btn(true)} disabled={busy} onClick={run}>{busy ? 'Working…' : 'Run backfill'}</button>
      </div>
      {status && !progress && (
        <div style={{ marginTop: 10, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {status.total} posted line item(s) still missing an invoice number
        </div>
      )}
      {progress && (
        <div style={{ marginTop: 10, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {busy ? 'Working… ' : '✓ Done. '}
          processed {progress.processed} · matched {progress.matched} · ambiguous {progress.ambiguous} · no candidate {progress.noCandidate}
        </div>
      )}
      {err && <div style={{ marginTop: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
    </div>
  )
}
